import React, { useEffect, useState } from "react";
import { Routes, Route, Link } from "react-router-dom";
import PortalLayout from "./PortalLayout";
import { StatCard, Panel, Badge, EmptyState, SkeletonCards } from "./BuyerPortal";
import PropertyForm from "./PropertyForm";
import SiteVisitsPanel from "@/components/SiteVisitsPanel";
import CPApplicationsPanel from "./CPApplicationsPanel";
import PartnerMembershipsPanel from "./PartnerMembershipsPanel";
import AdminMediaPanel from "./AdminMediaPanel";
import PartnerListingsPanel from "./PartnerListingsPanel";
import { AuctionsPanel, BrandsPanel } from "./AdminAuctionsBrands";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    LayoutDashboard, Users, Building2, Home as HomeIcon, ShieldCheck, MessageSquare,
    Wallet, ClipboardCheck, TrendingUp, Bell, FileCheck2, ArrowRight, Crown, Plus, Pencil, Trash2, CalendarCheck, Star, ClipboardList,
    LineChart as LineChartIcon, PieChart as PieIcon, BarChart3, Activity, Gavel, Store, BadgeCheck, Newspaper,
} from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";

const NAV = [
    { to: "/admin/dashboard",              l: "Overview",         icon: LayoutDashboard, end: true },
    { to: "/admin/dashboard/properties",   l: "Properties",       icon: HomeIcon },
    { to: "/admin/dashboard/auctions",     l: "Auction Properties", icon: Gavel },
    { to: "/admin/dashboard/brands",       l: "All Brands",       icon: Store },
    { to: "/admin/dashboard/media",        l: "Media Coverage",   icon: Newspaper },
    { to: "/admin/dashboard/leads",        l: "Lead Management",  icon: MessageSquare },
    { to: "/admin/dashboard/site-visits",  l: "Site Visits",      icon: CalendarCheck },
    { to: "/admin/dashboard/commissions",  l: "Commissions",      icon: Wallet },
    { to: "/admin/dashboard/buyers",       l: "Buyers",           icon: Users },
    { to: "/admin/dashboard/partners",     l: "Channel Partners", icon: Users },
    { to: "/admin/dashboard/cp-applications", l: "CP Applications", icon: ClipboardList },
    { to: "/admin/dashboard/partner-memberships", l: "Partner Memberships", icon: BadgeCheck },
    { to: "/admin/dashboard/partner-listings", l: "Partner Submissions", icon: Building2 },
    { to: "/admin/dashboard/builders",     l: "Builders",         icon: Building2 },
    { to: "/admin/dashboard/legal",        l: "Legal Verification", icon: ShieldCheck },
    { to: "/admin/dashboard/kyc",          l: "KYC Approvals",    icon: FileCheck2 },
];

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const GOLD = "#c19a5b";
const GOLD2 = "#e5c58a";

export default function AdminDashboard() {
    return (
        <Routes>
            <Route element={<PortalLayout role="admin" brand="Platform Admin" navItems={NAV}/>}>
                <Route index element={<Overview/>} />
                <Route path="properties" element={<PropertiesList/>} />
                <Route path="auctions" element={<AuctionsPanel/>} />
                <Route path="brands" element={<BrandsPanel/>} />
                <Route path="media" element={<AdminMediaPanel/>} />
                <Route path="leads" element={<LeadsList/>} />
                <Route path="site-visits" element={<SiteVisitsPanel endpoint="/admin/site-visits" title="Site Visits" canEdit={true} testid="admin-site-visits"/>} />
                <Route path="commissions" element={<CommissionsList/>} />
                <Route path="buyers" element={<UsersList role="buyer"/>} />
                <Route path="partners" element={<UsersList role="partner"/>} />
                <Route path="cp-applications" element={<CPApplicationsPanel/>} />
                <Route path="partner-memberships" element={<PartnerMembershipsPanel/>} />
                <Route path="partner-listings" element={<PartnerListingsPanel/>} />
                <Route path="builders" element={<UsersList role="builder"/>} />
                <Route path="legal" element={<LegalRequests/>} />
                <Route path="kyc" element={<KYCList/>} />
            </Route>
        </Routes>
    );
}

// ================================================================
// OVERVIEW — the beautiful analytics dashboard
// ================================================================
function Overview() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
    if (!data) return <SkeletonCards/>;
    const c = data.counts;

    return (
        <div className="space-y-6">
            {/* Top KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard Icon={HomeIcon}       v={c.properties} l="Properties Listed"  trend="+8 this wk" testid="kpi-props"/>
                <StatCard Icon={MessageSquare}  v={c.leads}      l="Total Leads"         trend="+42 this wk"/>
                <StatCard Icon={Users}          v={c.users}      l="Registered Users"    trend={`${c.buyers} buyers`}/>
                <StatCard Icon={ClipboardCheck} v={c.bookings}   l="Bookings"            trend="Confirmed"/>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <MiniStat Icon={Users}      v={c.buyers}   l="Buyers"          color="#3b82f6"/>
                <MiniStat Icon={Crown}      v={c.partners} l="Channel Partners" color={GOLD}/>
                <MiniStat Icon={Building2}  v={c.builders} l="Builders"        color="#a855f7"/>
                <MiniStat Icon={FileCheck2} v={c.pending_kyc} l="KYC Pending"  color="#eab308"/>
                <MiniStat Icon={ShieldCheck} v={c.pending_legal} l="Legal Reviews" color="#ef4444"/>
            </div>

            {/* Charts row 1 */}
            <div className="grid lg:grid-cols-3 gap-6">
                <ChartCard className="lg:col-span-2" title="Revenue Trend (Last 6 Months)" Icon={LineChartIcon} kpi={inr(data.revenue_series.reduce((a, s) => a + s.value, 0) * 100000)}>
                    <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={data.revenue_series}>
                            <defs>
                                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%"   stopColor={GOLD}  stopOpacity={0.4}/>
                                    <stop offset="100%" stopColor={GOLD} stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#0000000d"/>
                            <XAxis dataKey="label" stroke="#666" fontSize={11} tickLine={false} axisLine={false}/>
                            <YAxis stroke="#666" fontSize={11} tickLine={false} axisLine={false}/>
                            <Tooltip contentStyle={{ background: "#0a0d18", border: "none", borderRadius: 0, color: "#fff", fontSize: 12 }} formatter={(v) => [`₹${v} L`, "Revenue"]}/>
                            <Area type="monotone" dataKey="value" stroke={GOLD} strokeWidth={2.5} fill="url(#revenueGrad)"/>
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Lead Funnel" Icon={Activity}>
                    <div className="space-y-3 mt-2">
                        {data.funnel.map((f, i) => {
                            const max = data.funnel[0].value || 1;
                            const pct = (f.value / max) * 100;
                            return (
                                <div key={f.stage} data-testid={`funnel-${i}`}>
                                    <div className="flex items-center justify-between text-[12px] mb-1"><span className="font-bold">{f.stage}</span><span className="tabular-nums text-black/60">{f.value}</span></div>
                                    <div className="h-2 bg-black/6"><div className="h-full" style={{ width: pct + "%", background: `linear-gradient(90deg, ${GOLD}, ${GOLD2})` }}/></div>
                                </div>
                            );
                        })}
                    </div>
                </ChartCard>
            </div>

            {/* Charts row 2 */}
            <div className="grid lg:grid-cols-3 gap-6">
                <ChartCard title="Leads Volume (Last 6 Months)" Icon={BarChart3}>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={data.leads_series}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#0000000d"/>
                            <XAxis dataKey="label" stroke="#666" fontSize={11} tickLine={false} axisLine={false}/>
                            <YAxis stroke="#666" fontSize={11} tickLine={false} axisLine={false}/>
                            <Tooltip contentStyle={{ background: "#0a0d18", border: "none", color: "#fff", fontSize: 12 }}/>
                            <Bar dataKey="value" fill={GOLD} radius={[3, 3, 0, 0]}/>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Inventory by City" Icon={PieIcon}>
                    <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                            <Pie data={data.city_data} dataKey="value" nameKey="label" innerRadius={45} outerRadius={75} paddingAngle={3}>
                                {data.city_data.map((_, i) => <Cell key={i} fill={[GOLD, "#0a0d18", "#3b82f6", "#a855f7", "#22c55e", "#ef4444"][i % 6]}/>)}
                            </Pie>
                            <Tooltip contentStyle={{ background: "#0a0d18", border: "none", color: "#fff", fontSize: 12 }}/>
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="grid grid-cols-2 gap-1 mt-2 text-[10px]">
                        {data.city_data.map((c2, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                                <span className="w-2 h-2" style={{ background: [GOLD, "#0a0d18", "#3b82f6", "#a855f7", "#22c55e", "#ef4444"][i % 6] }}/>
                                <span className="text-black/70 truncate">{c2.label} ({c2.value})</span>
                            </div>
                        ))}
                    </div>
                </ChartCard>

                <ChartCard title="Portal Adoption" Icon={Users}>
                    <div className="grid grid-cols-3 gap-3 h-full items-end pt-8">
                        {[
                            { l: "Buyers",     v: c.buyers,   color: "#3b82f6" },
                            { l: "Partners",   v: c.partners, color: GOLD },
                            { l: "Builders",   v: c.builders, color: "#a855f7" },
                        ].map((b) => (
                            <div key={b.l} className="flex flex-col items-center">
                                <div className="w-full h-32 bg-black/5 relative overflow-hidden">
                                    <div className="absolute bottom-0 left-0 right-0" style={{ background: b.color, height: Math.min(100, b.v * 25) + "%" }}/>
                                </div>
                                <div className="font-bold text-lg mt-2 tabular-nums">{b.v}</div>
                                <div className="text-[10px] uppercase tracking-[0.16em] text-black/50">{b.l}</div>
                            </div>
                        ))}
                    </div>
                </ChartCard>
            </div>

            {/* Recent activity + Top partners */}
            <div className="grid lg:grid-cols-2 gap-6">
                <Panel title="Recent Leads" cta={{ to: "/admin/dashboard/leads", l: "View All" }}>
                    <ul className="divide-y divide-black/8">
                        {data.recent_leads.slice(0, 6).map((l, i) => (
                            <li key={i} className="py-3 flex items-center justify-between">
                                <div>
                                    <div className="font-bold text-[13px]">{l.name || "Anonymous"}</div>
                                    <div className="text-black/50 text-[11px]">{l.property_title || l.service || l.source} · {new Date(l.created_at).toLocaleDateString("en-IN")}</div>
                                </div>
                                <Badge tone="gold">{l.status || "new"}</Badge>
                            </li>
                        ))}
                        {data.recent_leads.length === 0 && <EmptyState msg="No leads yet."/>}
                    </ul>
                </Panel>
                <Panel title="Top Channel Partners" cta={{ to: "/admin/dashboard/partners", l: "View All" }}>
                    <ul className="divide-y divide-black/8">
                        {data.top_partners.slice(0, 6).map((p, i) => (
                            <li key={i} className="py-3 flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-bold ${i < 3 ? "bg-[var(--ebg-gold)] text-black" : "bg-black/5 text-black"}`}>#{i + 1}</div>
                                <div className="flex-1"><div className="font-bold text-[13px]">{p.name}</div><div className="text-black/50 text-[11px]">{p.partner_code || p.email}</div></div>
                                <Badge tone={p.kyc_status === "approved" ? "green" : "gold"}>{p.kyc_status || "pending"}</Badge>
                            </li>
                        ))}
                        {data.top_partners.length === 0 && <EmptyState msg="No partners yet."/>}
                    </ul>
                </Panel>
            </div>
        </div>
    );
}

// ================================================================
// Sub-views (Properties/Leads/Users/Legal/KYC)
// ================================================================
function PropertiesList() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);   // property object being edited
    const [creating, setCreating] = useState(false);
    const [kind, setKind] = useState("");
    const load = () => api.get("/admin/properties", { params: kind ? { kind } : {} }).then((r) => setItems(r.data.items || []));
    useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
    const toggleDealer = async (p) => {
        try { await api.patch(`/admin/properties/${p.id}/dealer`, { is_dealer: !p.is_dealer }); toast.success(p.is_dealer ? "Dealer tag removed" : "Tagged as Dealer Property"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const openEdit = async (id) => {
        try {
            const r = await api.get(`/admin/properties/${id}`);
            setEditing(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (id, title) => {
        if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
        try { await api.delete(`/admin/properties/${id}`); toast.success("Property deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const toggleFeatured = async (p) => {
        const next = !p.featured;
        try {
            await api.patch(`/admin/properties/${p.id}/featured`, { featured: next });
            toast.success(next ? `"${p.title}" marked as Signature` : `"${p.title}" removed from Signature`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Panel
                title={`All Properties (${items.length})`}
                cta={{ to: "/properties", l: "View Public Portfolio" }}
                testid="admin-properties"
            >
                <div className="flex items-center justify-between mb-4 -mt-2">
                    <div className="text-[11px] text-black/55">
                        <Star size={11} className="inline mb-0.5 text-[var(--ebg-gold)]" fill="currentColor"/> Signature Projects appear on the Home page ({items.filter((x) => x.featured && x.listing_type !== "auction" && !["Land", "Plot"].includes(x.type)).length} active)
                    </div>
                    <button
                        onClick={() => setCreating(true)}
                        data-testid="admin-new-property-btn"
                        className="bg-black text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black inline-flex items-center gap-2"
                    >
                        <Plus size={13}/> New Property
                    </button>
                </div>
                <div className="flex gap-2 mb-4" data-testid="admin-prop-kind-filter">
                    {[["", "All"], ["dealer", "Dealer Properties"], ["land", "Land"]].map(([k, l]) => (
                        <button key={k} onClick={() => setKind(k)} data-testid={`admin-prop-kind-${k || "all"}`} className={`px-3 py-2 text-[10px] uppercase tracking-[0.18em] font-bold border ${kind === k ? "bg-black text-white border-black" : "border-black/15 hover:border-black"}`}>{l}</button>
                    ))}
                </div>
                <div className="overflow-x-auto"><table className="w-full min-w-[900px]">
                    <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                        <th className="pb-3">Property</th><th className="pb-3">City</th><th className="pb-3">Price</th><th className="pb-3">Status</th><th className="pb-3">Trust</th><th className="pb-3">Dealer</th><th className="pb-3">Signature</th><th className="pb-3">Actions</th>
                    </tr></thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-b border-black/8 last:border-b-0" data-testid={`admin-prop-row-${p.slug || p.id}`}>
                                <td className="py-3 font-bold">{p.title}{p.submitted_by_role === "partner" && <div className="text-[11px] font-normal text-black/50" data-testid={`admin-prop-partner-${p.slug}`}>Partner: {p.submitted_by_name} · {String(p.submitted_by).slice(0, 8)}</div>}</td>
                                <td className="py-3 text-black/70">{p.city}</td>
                                <td className="py-3 font-bold text-[var(--ebg-gold)]">{p.price_display}</td>
                                <td className="py-3"><Badge tone="green">{p.status}</Badge></td>
                                <td className="py-3 font-bold tabular-nums">{p.trust_score?.total || 0}/100</td>
                                <td className="py-3"><button onClick={() => toggleDealer(p)} data-testid={`admin-prop-dealer-${p.slug}`} className={`text-[9px] uppercase tracking-[0.14em] font-bold px-2 py-1 border ${p.is_dealer ? "bg-black text-white border-black" : "border-black/15 text-black/40 hover:border-black"}`}>{p.is_dealer ? "Dealer" : "Tag"}</button></td>
                                <td className="py-3">
                                    <button
                                        onClick={() => toggleFeatured(p)}
                                        data-testid={`admin-prop-signature-${p.slug || p.id}`}
                                        title={p.featured ? "Remove from Home page Signature Projects" : "Mark as Signature Project (shows on Home page)"}
                                        className={`w-8 h-8 inline-flex items-center justify-center border transition-colors ${p.featured ? "bg-[var(--ebg-gold)] border-[var(--ebg-gold)] text-black" : "bg-white border-black/15 text-black/30 hover:border-[var(--ebg-gold)] hover:text-[var(--ebg-gold)]"}`}
                                    >
                                        <Star size={14} fill={p.featured ? "currentColor" : "none"}/>
                                    </button>
                                </td>
                                <td className="py-3">
                                    <div className="flex gap-1">
                                        <button onClick={() => openEdit(p.slug || p.id)} data-testid={`admin-prop-edit-${p.slug || p.id}`} className="text-[10px] px-2 py-1 border border-black/15 hover:border-black inline-flex items-center gap-1"><Pencil size={11}/> Edit</button>
                                        <button onClick={() => del(p.id, p.title)} data-testid={`admin-prop-delete-${p.slug || p.id}`} className="text-[10px] px-2 py-1 border border-red-500/40 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center gap-1"><Trash2 size={11}/> Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table></div>
            </Panel>

            {creating && <PropertyForm onClose={() => setCreating(false)} onSaved={load}/>}
            {editing && <PropertyForm existing={editing} onClose={() => setEditing(null)} onSaved={load}/>}
        </>
    );
}

function LeadsList() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/admin/leads").then((r) => setItems(r.data.items || [])).catch(() => {});
    useEffect(() => { load(); }, []);
    const setStatus = async (id, status) => {
        try { await api.patch(`/admin/leads/${id}`, { status }); toast.success(`Updated to ${status}`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (id) => {
        if (!window.confirm("Delete this lead?")) return;
        try { await api.delete(`/admin/leads/${id}`); toast.success("Lead deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const OPTS = ["new", "contacted", "site_visit", "negotiating", "won", "lost"];
    const [src, setSrc] = useState("all");
    const sources = [...new Set(items.map((l) => l.source).filter(Boolean))].sort();
    const shown = src === "all" ? items : items.filter((l) => l.source === src);
    const label = (s) => (s || "").split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    return (
        <Panel title={`All Leads (${shown.length})`} testid="admin-leads">
            <div className="flex items-center gap-2 mb-4 -mt-2">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/50">Source</span>
                <select value={src} onChange={(e) => setSrc(e.target.value)} data-testid="lead-source-filter" className="text-[12px] border border-black/15 px-2 py-1.5 bg-white outline-none focus:border-black">
                    <option value="all">All sources</option>
                    {sources.map((s) => <option key={s} value={s}>{label(s)}</option>)}
                </select>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px] text-[13px]">
                    <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                        <th className="pb-3">Name</th><th className="pb-3">Mobile / Email</th><th className="pb-3">City</th><th className="pb-3">User Type</th><th className="pb-3">Property / Service</th><th className="pb-3">Source</th><th className="pb-3">Submitted</th><th className="pb-3">Status</th><th className="pb-3">Actions</th>
                    </tr></thead>
                    <tbody>
                        {shown.map((l) => (
                            <tr key={l.id} className="border-b border-black/8 last:border-b-0 align-top" data-testid={`lead-row-${l.id}`}>
                                <td className="py-3 font-bold">{l.name}</td>
                                <td className="py-3 text-black/70"><div data-testid={`lead-phone-${l.id}`}>{l.phone || "—"}</div><div className="text-[11px] text-black/50">{l.email || "—"}</div></td>
                                <td className="py-3 text-black/70" data-testid={`lead-city-${l.id}`}>{l.city || "—"}</td>
                                <td className="py-3" data-testid={`lead-usertype-${l.id}`}>{l.user_type || "—"}</td>
                                <td className="py-3" data-testid={`lead-subject-${l.id}`}>{l.property_title || l.service || "—"}{l.brochure_name && <div className="text-[11px] text-[var(--ebg-gold)]" data-testid={`lead-brochure-${l.id}`}>{l.brochure_name}</div>}</td>
                                <td className="py-3 text-black/60" data-testid={`lead-source-${l.id}`}>{label(l.source)}{l.property_category && l.property_category !== "property" && <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--ebg-gold)]" data-testid={`lead-category-${l.id}`}>{l.property_category === "dealer" ? "Dealer Property" : label(l.property_category)}</div>}</td>
                                <td className="py-3 text-black/60 whitespace-nowrap" data-testid={`lead-date-${l.id}`}>{l.created_at ? new Date(l.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                                <td className="py-3">
                                    <select value={l.status || "new"} onChange={(e) => setStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className="text-[11px] border border-black/15 px-2 py-1.5 bg-white outline-none focus:border-black">
                                        {OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td className="py-3">
                                    <button onClick={() => del(l.id)} data-testid={`lead-delete-${l.id}`} className="text-[10px] px-2 py-1 border border-red-500/40 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center gap-1"><Trash2 size={11}/></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Panel>
    );
}

function CommissionsList() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/admin/commissions").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    useEffect(() => { load(); }, []);
    const markPaid = async (id) => {
        try { await api.patch(`/admin/commissions/${id}`, { status: "paid" }); toast.success("Commission marked paid"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const markPending = async (id) => {
        try { await api.patch(`/admin/commissions/${id}`, { status: "pending" }); toast.success("Reverted to pending"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <Panel title={`Commissions (${items.length})`} testid="admin-commissions">
            {items.length === 0 ? <EmptyState msg="No commission entries yet. Bookings will generate commission records automatically."/> : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px]">
                        <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                            <th className="pb-3">Partner</th><th className="pb-3">Property</th><th className="pb-3">Amount</th><th className="pb-3">Status</th><th className="pb-3">Actions</th>
                        </tr></thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="border-b border-black/8 last:border-b-0" data-testid={`comm-row-${c.id}`}>
                                    <td className="py-3 font-bold">{c.partner_name || c.partner_id}</td>
                                    <td className="py-3 text-black/70">{c.property_title || "—"}</td>
                                    <td className="py-3 font-bold text-[var(--ebg-gold)]">{inr(c.amount || 0)}</td>
                                    <td className="py-3"><Badge tone={c.status === "paid" ? "green" : "gold"}>{c.status || "pending"}</Badge></td>
                                    <td className="py-3">
                                        {c.status === "paid" ? (
                                            <button onClick={() => markPending(c.id)} data-testid={`comm-revert-${c.id}`} className="text-[10px] px-2 py-1 border border-black/15 hover:border-black">Revert to Pending</button>
                                        ) : (
                                            <button onClick={() => markPaid(c.id)} data-testid={`comm-paid-${c.id}`} className="text-[10px] px-2 py-1 bg-green-600 text-white hover:bg-green-700">Mark as Paid</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Panel>
    );
}

function UsersList({ role }) {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/admin/users", { params: { role } }).then((r) => setItems(r.data.items || [])); }, [role]);
    return (
        <Panel title={`${role[0].toUpperCase() + role.slice(1)}s (${items.length})`} testid={`admin-${role}s`}>
            <table className="w-full">
                <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                    <th className="pb-3">Name</th><th className="pb-3">Email</th><th className="pb-3">Phone</th><th className="pb-3">City</th>{role === "partner" && <th className="pb-3">KYC</th>}
                </tr></thead>
                <tbody>
                    {items.map((u) => (
                        <tr key={u.id} className="border-b border-black/8 last:border-b-0">
                            <td className="py-3 font-bold">{u.name}</td>
                            <td className="py-3 text-black/70">{u.email}</td>
                            <td className="py-3 text-black/60">{u.phone || "—"}</td>
                            <td className="py-3 text-black/60">{u.city || "—"}</td>
                            {role === "partner" && <td className="py-3"><Badge tone={u.kyc_status === "approved" ? "green" : "gold"}>{u.kyc_status || "pending"}</Badge></td>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </Panel>
    );
}

function LegalRequests() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/legal-requests").then((r) => setItems(r.data.items || []));
    useEffect(() => { load(); }, []);
    const setStatus = async (id, status) => {
        try { await api.put(`/admin/legal-requests/${id}`, { status }); toast.success("Updated"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <Panel title={`Legal Verification Queue (${items.length})`} testid="admin-legal">
            {items.length === 0 ? <EmptyState msg="No legal verification requests."/> : (
                <table className="w-full">
                    <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                        <th className="pb-3">Property</th><th className="pb-3">Requester</th><th className="pb-3">City</th><th className="pb-3">Status</th><th className="pb-3">Actions</th>
                    </tr></thead>
                    <tbody>
                        {items.map((r) => (
                            <tr key={r.id} className="border-b border-black/8 last:border-b-0">
                                <td className="py-3">{r.property_title}</td>
                                <td className="py-3 text-black/70">{r.user_email}</td>
                                <td className="py-3 text-black/60">{r.city || "—"}</td>
                                <td className="py-3"><Badge tone={r.status === "verified" ? "green" : r.status === "rejected" ? "red" : "gold"}>{r.status}</Badge></td>
                                <td className="py-3">
                                    <div className="flex gap-1">
                                        <button onClick={() => setStatus(r.id, "in_progress")} data-testid={`legal-${r.id}-progress`} className="text-[10px] px-2 py-1 border border-black/15 hover:border-black">Start</button>
                                        <button onClick={() => setStatus(r.id, "verified")} data-testid={`legal-${r.id}-verify`}  className="text-[10px] px-2 py-1 bg-green-600 text-white">Verify</button>
                                        <button onClick={() => setStatus(r.id, "rejected")} data-testid={`legal-${r.id}-reject`}  className="text-[10px] px-2 py-1 bg-red-600 text-white">Reject</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </Panel>
    );
}

function KYCList() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/admin/users", { params: { role: "partner" } }).then((r) => setItems(r.data.items || []));
    useEffect(() => { load(); }, []);
    const decide = async (id, status) => {
        try { await api.put(`/admin/users/${id}/kyc`, { status }); toast.success("KYC " + status); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <Panel title="KYC Approval Queue" testid="admin-kyc">
            <table className="w-full">
                <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                    <th className="pb-3">Partner</th><th className="pb-3">Email</th><th className="pb-3">Code</th><th className="pb-3">Status</th><th className="pb-3">Actions</th>
                </tr></thead>
                <tbody>
                    {items.map((u) => (
                        <tr key={u.id} className="border-b border-black/8 last:border-b-0">
                            <td className="py-3 font-bold">{u.name}</td>
                            <td className="py-3 text-black/70">{u.email}</td>
                            <td className="py-3 text-black/60">{u.partner_code || "—"}</td>
                            <td className="py-3"><Badge tone={u.kyc_status === "approved" ? "green" : u.kyc_status === "rejected" ? "red" : "gold"}>{u.kyc_status || "pending"}</Badge></td>
                            <td className="py-3">
                                <div className="flex gap-1">
                                    <button onClick={() => decide(u.id, "approved")} data-testid={`kyc-${u.id}-approve`} className="text-[10px] px-2 py-1 bg-green-600 text-white">Approve</button>
                                    <button onClick={() => decide(u.id, "rejected")} data-testid={`kyc-${u.id}-reject`}  className="text-[10px] px-2 py-1 bg-red-600 text-white">Reject</button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Panel>
    );
}

// ------- Small helpers -------
function MiniStat({ Icon, v, l, color }) {
    return (
        <div className="bg-white border border-black/8 p-4 flex items-center gap-3" data-testid={`ministat-${l.toLowerCase().replace(/\s/g, "-")}`}>
            <span className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: color }}>
                <Icon size={14}/>
            </span>
            <div>
                <div className="font-serif text-xl tabular-nums leading-none">{v}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-black/50 mt-1">{l}</div>
            </div>
        </div>
    );
}

function ChartCard({ title, Icon, kpi, children, className = "" }) {
    return (
        <section className={`bg-white border border-black/8 p-6 ${className}`}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Icon size={15} className="text-[var(--ebg-gold)]"/>
                    <h3 className="text-[13px] uppercase tracking-[0.2em] font-bold text-black">{title}</h3>
                </div>
                {kpi && <div className="font-serif text-lg text-[var(--ebg-gold)] tabular-nums">{kpi}</div>}
            </div>
            {children}
        </section>
    );
}
